import { useState } from 'react';
import type { TableProps } from 'antd';
import { UnlockOutlined } from '@ant-design/icons';
import { UserLock } from 'lucide-react';

import { DataTable, MenuThaoTac, Tile, TileHeader } from '../../../components/shared';
import { Button, Dialog, Empty } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { useKhoaTam, useMoKhoaTam } from '../hooks/useKhoaTam';
import { tinhTrangKhoaTam } from '../nghiep-vu/khoa-tam';
import type { TaiKhoanKhoaTam } from '../types';

/**
 * Hộp xác nhận mở khoá tạm: tiêu đề hỏi, thân nói hậu quả, nút nhắc lại việc.
 * Lỗi (`403`, `404 USER_NOT_FOUND`, `503`) hiện **trong** hộp bằng câu của
 * server, hộp không đóng.
 */
function HopMoKhoaTam({
  open,
  nguoi,
  dangChay,
  loi,
  onClose,
  onXacNhan,
}: {
  open: boolean;
  /** Giữ nguyên khi hộp đang đóng, để tiêu đề không nháy trống lúc mờ dần. */
  nguoi: TaiKhoanKhoaTam | null;
  dangChay: boolean;
  loi: unknown;
  onClose: () => void;
  onXacNhan: (nguoi: TaiKhoanKhoaTam) => void;
}) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const ten = nguoi?.full_name ?? '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('baoMat.khoaTam.xnTieuDe', { ten })}
      width={480}
      destroyOnHidden
      footer={
        <div className="flex flex-wrap justify-end gap-3">
          <Button variant="outline" size="md" onClick={onClose}>
            {t('chung.huy')}
          </Button>
          <Button variant="primary" size="md" loading={dangChay} onClick={() => nguoi && onXacNhan(nguoi)}>
            {t('baoMat.khoaTam.moKhoa')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3 pt-2">
        <p className="text-sm leading-relaxed text-content-secondary">
          {t('baoMat.khoaTam.xnThan', { ten, so: soVN(nguoi?.num_failures ?? 0, locale) })}
        </p>
        <p className="text-xs text-content-muted">{t('baoMat.khoaTam.xnGhiChu')}</p>
        {Boolean(loi) && <LoiTrongHop>{dichLoi(loi)}</LoiTrongHop>}
      </div>
    </Dialog>
  );
}

/**
 * "Tài khoản đang bị khoá tạm" ở thẻ Đăng nhập (ADR-0059 mục 5.3, mục 8).
 *
 * user-service tra theo khúc hồ sơ nên trang có thể rỗng mà vẫn còn trang sau:
 * chỉ báo "không có ai" khi đã tra hết; chưa hết thì mời "Tra tiếp". Mục mở
 * khoá ẩn hẳn khi không có quyền sửa (ADR-UI-0005).
 */
export function KhoiKhoaTam({ tenantId, duocSua }: { tenantId: string; duocSua: boolean }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const q = useKhoaTam(tenantId);
  const moKhoa = useMoKhoaTam(tenantId);
  const [hop, setHop] = useState<{ mo: boolean; nguoi: TaiKhoanKhoaTam | null }>({ mo: false, nguoi: null });

  // Lỗi khi đã có trang là lỗi của lần "Tra tiếp": giữ bảng, báo cạnh nút.
  const loiTaiDau = q.error && !q.data;
  const dangTaiDau = q.isLoading;
  const tinhTrang = tinhTrangKhoaTam(q.dong.length, Boolean(q.hasNextPage));

  const dongHop = () => {
    setHop((h) => ({ ...h, mo: false }));
    moKhoa.reset();
  };

  const nutTraTiep = (
    <Button variant="outline" size="sm" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
      {q.isFetchNextPageError ? t('chung.thuLai') : t('baoMat.khoaTam.traTiep')}
    </Button>
  );

  const cot: TableProps<TaiKhoanKhoaTam>['columns'] = [
    {
      title: t('baoMat.khoaTam.cotNguoiDung'),
      width: 260,
      render: (_v, row) => (
        <div className="flex min-w-0 max-w-xs flex-col">
          <span className="truncate text-sm font-medium text-content-primary">{row.full_name}</span>
          <span className="truncate text-2xs text-content-muted">
            {row.email ?? t('baoMat.khoaTam.chuaDocDuocEmail')}
          </span>
        </div>
      ),
    },
    {
      title: t('baoMat.khoaTam.cotSoLanSai'),
      dataIndex: 'num_failures',
      width: 110,
      align: 'right',
      render: (v: number) => <span className="text-xs tabular-nums text-content-secondary">{soVN(v, locale)}</span>,
    },
    {
      title: t('baoMat.khoaTam.cotLanSaiCuoi'),
      dataIndex: 'last_failure',
      width: 160,
      render: (v: string | null) => (
        <span className="whitespace-nowrap text-xs text-content-muted">{ngayGioVN(v, locale)}</span>
      ),
    },
    {
      title: t('baoMat.khoaTam.cotIp'),
      dataIndex: 'last_ip_failure',
      width: 200,
      render: (v: string | null) => (
        <span className="break-all font-mono text-xs text-content-secondary">{v ?? KHONG_CO}</span>
      ),
    },
    ...(duocSua
      ? [
          {
            title: t('chung.thaoTac'),
            width: 72,
            align: 'center' as const,
            render: (_v: unknown, row: TaiKhoanKhoaTam) => (
              <MenuThaoTac
                tenBanGhi={row.full_name}
                muc={[
                  {
                    khoa: 'mo-khoa',
                    nhan: t('baoMat.khoaTam.moKhoa'),
                    icon: <UnlockOutlined />,
                    onChon: () => {
                      // Lỗi của lần trước (có thể của người khác) không được hiện ở hộp mới.
                      moKhoa.reset();
                      setHop({ mo: true, nguoi: row });
                    },
                  },
                ]}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <Tile className="overflow-hidden">
      <TileHeader
        icon={<UserLock size={16} />}
        tone="primary"
        title={t('baoMat.khoaTam.tieuDe')}
      />
      {loiTaiDau ? (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
          <span className="min-w-0 text-sm text-danger-700">
            {t('baoMat.khoaTam.loi', { loi: dichLoi(q.error) })}
          </span>
          <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
            {t('chung.thuLai')}
          </Button>
        </div>
      ) : (
        <>
          <DataTable<TaiKhoanKhoaTam>
            caption={t('baoMat.khoaTam.bangCaption')}
            rowKey="user_id"
            dataSource={q.dong}
            columns={cot}
            loading={dangTaiDau}
            size="small"
            pagination={false}
            locale={{
              emptyText: dangTaiDau ? (
                ' '
              ) : tinhTrang === 'chua-thay' ? (
                <Empty
                  icon={UserLock}
                  title={t('baoMat.khoaTam.chuaThay')}
                  description={t('baoMat.khoaTam.chuaThayMo')}
                  action={nutTraTiep}
                />
              ) : (
                <Empty icon={UserLock} title={t('baoMat.khoaTam.rong')} description={t('baoMat.khoaTam.rongMo')} />
              ),
            }}
          />
          {q.isFetchNextPageError && (
            <p role="alert" className="border-t border-default px-5 py-2.5 text-xs text-danger-700">
              {t('baoMat.khoaTam.loiTraTiep', { loi: dichLoi(q.error) })}
            </p>
          )}
          {tinhTrang === 'co-du-lieu' && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-default px-5 py-3">
              <span className="text-xs text-content-muted">
                {t(q.hasNextPage ? 'baoMat.khoaTam.dangHienCoThem' : 'baoMat.khoaTam.dangHien', {
                  so: soVN(q.dong.length, locale),
                })}
              </span>
              {q.hasNextPage && nutTraTiep}
            </div>
          )}
        </>
      )}

      <HopMoKhoaTam
        open={hop.mo}
        nguoi={hop.nguoi}
        dangChay={moKhoa.isPending}
        loi={moKhoa.error}
        onClose={dongHop}
        onXacNhan={(nguoi) => moKhoa.mutate(nguoi, { onSuccess: () => setHop((h) => ({ ...h, mo: false })) })}
      />
    </Tile>
  );
}
